import * as RadixDialog from "@radix-ui/react-dialog";
import { useEffect, useState } from "react";
import {
  BriefcaseBusiness,
  ChevronRight,
  Database,
  Home,
  Info,
  Menu,
  PenLine,
  Settings,
  ShieldCheck,
  Wifi,
  WifiOff,
  X,
  type LucideIcon,
} from "lucide-react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Button } from "./Button";
import { CommandPalette } from "./CommandPalette";
import { UpdateToast } from "./UpdateToast";
import { UserMenu } from "./UserMenu";
import { useAppStore } from "../store/appStore";

const navItems: { to: string; label: string; icon: LucideIcon }[] = [
  { to: "/", label: "Home", icon: Home },
  { to: "/post", label: "Post", icon: PenLine },
  { to: "/jobs", label: "Jobs", icon: BriefcaseBusiness },
];

const utilityItems: { to: string; label: string; icon: LucideIcon }[] = [
  { to: "/safety", label: "Safety tips", icon: ShieldCheck },
  { to: "/data", label: "Export", icon: Database },
  { to: "/settings", label: "Settings", icon: Settings },
  { to: "/about", label: "About", icon: Info },
];

const allNavItems = [...navItems, ...utilityItems];

function getSectionName(pathname: string) {
  if (pathname === "/") {
    return "Home";
  }
  if (pathname.startsWith("/post")) {
    return "Post";
  }
  if (pathname.startsWith("/jobs")) {
    return "Jobs";
  }
  if (pathname.startsWith("/contractors")) {
    return "Profiles";
  }
  if (pathname.startsWith("/safety")) {
    return "Safety tips";
  }
  if (pathname.startsWith("/data")) {
    return "Export";
  }
  if (pathname.startsWith("/settings")) {
    return "Settings";
  }
  if (pathname.startsWith("/about")) {
    return "About";
  }
  return "Not found";
}

function ConnectionIndicator({
  offline,
  compact = false,
}: {
  offline: boolean;
  compact?: boolean;
}) {
  const Icon = offline ? WifiOff : Wifi;

  return (
    <span
      className={`inline-flex min-h-8 items-center gap-2 rounded-pill px-2.5 text-xs font-semibold ${
        offline
          ? "border border-warning-border bg-warning-bg text-warning shadow-[0_0_0_4px_hsla(32,84%,37%,0.12)]"
          : "text-text-subtle"
      }`}
    >
      <span className="relative flex h-2 w-2">
        {offline ? (
          <span className="absolute inline-flex h-full w-full animate-[ping_1.6s_cubic-bezier(0,0,0.2,1)_infinite] rounded-pill bg-current opacity-25" />
        ) : null}
        <span className="relative inline-flex h-2 w-2 rounded-pill bg-current" />
      </span>
      <Icon aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />
      {compact ? (
        <span className="sr-only">{offline ? "Offline" : "Online"}</span>
      ) : offline ? (
        "Offline"
      ) : (
        "Online"
      )}
    </span>
  );
}

function Brand({ section }: { section: string }) {
  return (
    <NavLink className="group flex items-center gap-3" to="/" aria-label="ClearScope home">
      <span className="brand-mark flex h-8 w-8 items-center justify-center rounded-[var(--radius-control)] text-white">
        <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 32 32">
          <path
            d="M6 8h4M22 8h4M6 24h4M22 24h4"
            fill="none"
            opacity="0.68"
            stroke="currentColor"
            strokeLinecap="round"
            strokeWidth="2"
          />
          <path
            d="M11 16.5l3.5 3.5L22 12"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2.4"
          />
        </svg>
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="font-sans text-[1.0625rem] font-semibold text-text">ClearScope</span>
        <span className="cell-label hidden sm:block">{section}</span>
      </span>
    </NavLink>
  );
}

function isRouteActive(pathname: string, to: string) {
  if (to === "/") {
    return pathname === "/";
  }
  return pathname === to || pathname.startsWith(`${to}/`);
}

function DesktopNav() {
  return (
    <nav aria-label="Primary navigation" className="hidden items-center gap-1 md:flex xl:hidden">
      {navItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          className={({ isActive }) =>
            `group relative inline-flex min-h-12 items-center gap-2 rounded-[var(--radius-control)] px-3 text-sm font-semibold transition after:absolute after:inset-x-3 after:bottom-0 after:h-0.5 after:origin-left after:rounded-pill after:bg-accent-600 after:transition-transform ${
              isActive
                ? "bg-surface-muted text-text after:scale-x-100"
                : "text-text-muted after:scale-x-0 hover:bg-surface-muted/70 hover:text-text"
            }`
          }
        >
          {() => (
            <>
              <item.icon aria-hidden="true" className="h-4 w-4" strokeWidth={1.5} />
              {item.label}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function DesktopRail({ pathname }: { pathname: string }) {
  return (
    <aside className="fixed bottom-5 left-5 top-5 z-30 hidden w-[4.25rem] flex-col rounded-[var(--radius-overlay)] border border-border bg-surface/88 p-2 shadow-[var(--shadow-3)] backdrop-blur-2xl after:absolute after:-right-5 after:bottom-0 after:top-0 after:w-px after:bg-border xl:flex">
      <NavLink
        aria-label="ClearScope home"
        className="brand-mark mb-3 flex h-11 w-full items-center justify-center rounded-[var(--radius-surface)] text-white"
        to="/"
      >
        <svg aria-hidden="true" className="h-5 w-5" viewBox="0 0 32 32">
          <path
            d="M6 8h4M22 8h4M6 24h4M22 24h4"
            fill="none"
            opacity="0.68"
            stroke="currentColor"
            strokeLinecap="round"
            strokeWidth="2"
          />
          <path
            d="M11 16.5l3.5 3.5L22 12"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2.4"
          />
        </svg>
      </NavLink>
      <nav aria-label="Workspace navigation" className="grid gap-2">
        {allNavItems.map((item) => {
          const active = isRouteActive(pathname, item.to);

          return (
            <NavLink
              key={item.to}
              aria-label={item.label}
              className={`group relative flex h-12 items-center justify-center rounded-[var(--radius-control)] border text-text-muted transition ${
                active
                  ? "border-accent-200 bg-accent-50 text-accent-700 shadow-[var(--shadow-card)] before:absolute before:left-1 before:top-1/2 before:h-6 before:w-0.5 before:-translate-y-1/2 before:rounded-pill before:bg-accent-700 before:transition-transform before:duration-200 before:ease-[var(--ease-spring)]"
                  : "border-transparent hover:border-border hover:bg-surface-muted hover:text-text"
              }`}
              title={item.label}
              to={item.to}
            >
              <item.icon aria-hidden="true" className="h-5 w-5" strokeWidth={1.5} />
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}

function MobileNav({ offline }: { offline: boolean }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const mobileItems = [...navItems, ...utilityItems];
  const current =
    mobileItems.find((item) => item.to === location.pathname) ??
    (location.pathname.startsWith("/jobs")
      ? mobileItems.find((item) => item.to === "/jobs")
      : null);

  return (
    <RadixDialog.Root open={open} onOpenChange={setOpen}>
      <RadixDialog.Trigger asChild>
        <Button aria-label="Open navigation menu" className="md:hidden" size="sm" variant="ghost">
          <Menu aria-hidden="true" className="h-5 w-5" />
        </Button>
      </RadixDialog.Trigger>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="dialog-overlay fixed inset-0 z-50 bg-[var(--color-overlay)] backdrop-blur-2xl" />
        <RadixDialog.Content className="fixed bottom-0 left-0 right-0 z-50 flex max-h-[88vh] flex-col overflow-auto rounded-t-[24px] border-t border-border bg-surface-raised p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-[var(--shadow-3)]">
          <div className="flex items-center justify-between gap-3">
            <div>
              <Brand section={current ? current.label : "Navigation"} />
              <RadixDialog.Title className="mt-3 text-sm font-semibold text-text-muted">
                {current ? current.label : "Navigation"}
              </RadixDialog.Title>
            </div>
            <RadixDialog.Close asChild>
              <Button aria-label="Close navigation menu" size="sm" variant="ghost">
                <X aria-hidden="true" className="h-4 w-4" />
              </Button>
            </RadixDialog.Close>
          </div>
          <div className="mt-6 grid gap-2">
            {mobileItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `flex min-h-14 items-center justify-between rounded-card border px-4 text-sm font-semibold transition ${
                    isActive
                      ? "border-accent-200 bg-accent-50 text-accent-700"
                      : "border-border bg-surface text-text hover:bg-surface-muted"
                  }`
                }
                onClick={() => setOpen(false)}
              >
                <span className="flex items-center gap-3">
                  <item.icon aria-hidden="true" className="h-4 w-4" />
                  {item.label}
                </span>
                <ChevronRight
                  aria-hidden="true"
                  className="h-4 w-4 text-text-subtle"
                  strokeWidth={1.5}
                />
              </NavLink>
            ))}
          </div>
          <div className="mt-auto rounded-card border border-border bg-surface p-3 shadow-[var(--shadow-card)]">
            <ConnectionIndicator offline={offline} />
          </div>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

function BottomTabBar({ pathname }: { pathname: string }) {
  return (
    <nav
      aria-label="Mobile primary navigation"
      className="fixed inset-x-3 bottom-3 z-40 grid grid-cols-3 gap-1 rounded-[22px] border border-border bg-surface/92 p-1.5 shadow-[var(--shadow-3)] backdrop-blur-2xl md:hidden"
      style={{ paddingBottom: "calc(0.375rem + env(safe-area-inset-bottom))" }}
    >
      {navItems.map((item) => {
        const active = isRouteActive(pathname, item.to);

        return (
          <NavLink
            key={item.to}
            aria-label={item.label}
            className={`flex min-h-12 flex-col items-center justify-center gap-1 rounded-[16px] text-xs font-semibold transition ${
              active
                ? "bg-accent-50 text-accent-700 shadow-[var(--shadow-card)]"
                : "text-text-muted hover:bg-surface-muted hover:text-text"
            }`}
            to={item.to}
          >
            <item.icon aria-hidden="true" className="h-4 w-4" strokeWidth={1.6} />
            {item.label}
          </NavLink>
        );
      })}
    </nav>
  );
}

export function AppShell() {
  const offline = useAppStore((state) => state.offline);
  const setOffline = useAppStore((state) => state.setOffline);
  const [elevated, setElevated] = useState(false);
  const location = useLocation();
  const showDesktopRail = true;

  useEffect(() => {
    const update = () => setOffline(!navigator.onLine);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    update();
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, [setOffline]);

  useEffect(() => {
    const update = () => setElevated(window.scrollY > 8);
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, []);

  return (
    <div className={`min-h-screen ${showDesktopRail ? "xl:pl-[6.5rem]" : ""}`}>
      <a className="skip-link" href="#main">
        Skip to main content
      </a>
      {showDesktopRail ? <DesktopRail pathname={location.pathname} /> : null}
      <header
        className={`sticky top-0 z-40 border-b bg-surface/80 backdrop-blur-2xl transition-[border-color,box-shadow,background-color] duration-200 ${
          elevated
            ? "border-accent-200/70 bg-surface/95 shadow-[inset_0_-1px_0_var(--color-border)]"
            : "border-border/80 shadow-xs"
        }`}
      >
        <p className="sr-only" aria-live="polite">
          {offline ? "Application is running in offline mode." : "Application is online."}
        </p>
        <div className="mx-auto grid min-h-16 max-w-[var(--page-max)] grid-cols-[1fr_auto_auto] items-center gap-3 px-4 py-2 md:grid-cols-[1fr_auto_1fr] md:gap-4 md:px-6 xl:px-10">
          <Brand section={getSectionName(location.pathname)} />
          <DesktopNav />
          <div className="hidden justify-end gap-2 md:flex">
            <CommandPalette />
            <ConnectionIndicator offline={offline} />
            <UserMenu />
          </div>
          <NavLink
            className="inline-flex h-9 items-center rounded-pill border border-border bg-surface-raised px-3 text-sm font-semibold text-text shadow-[var(--shadow-card)] md:hidden"
            to="/jobs"
          >
            Jobs
          </NavLink>
          <MobileNav offline={offline} />
        </div>
      </header>
      <main
        id="main"
        className="mx-auto max-w-[var(--page-max)] px-4 pb-[calc(var(--space-page-y)+5.5rem+env(safe-area-inset-bottom))] pt-[var(--space-page-y)] md:px-6 md:pb-[var(--space-page-y)] xl:px-10"
      >
        <div key={location.pathname} className="route-enter">
          <Outlet />
        </div>
      </main>
      <BottomTabBar pathname={location.pathname} />
      <UpdateToast />
    </div>
  );
}
